'use client';
import { useRouter } from 'next/navigation';
import { type Address } from 'viem';
import { type ChainType, chainSettings } from '@/constants/chain-specific';
import { type NFTMetadata } from '@/util/nftMetadata';
import { getCollectionByAddress } from '@/constants/marketplace/collections';

interface NFTViewerData {
  metadata: NFTMetadata;
  owner: Address | null;
  isERC1155: boolean;
  balance?: bigint;
}

interface UseNFTViewerReturn {
  openNFTModal: (params: {
    chainId: ChainType;
    contractAddress: Address;
    tokenId: bigint;
    initialData?: NFTViewerData;
  }) => void;
  openNFTPage: (params: {
    chainId: ChainType;
    contractAddress: Address;
    tokenId: bigint;
  }) => void;
}

export function useNFTViewer(): UseNFTViewerReturn {
  const router = useRouter();

  const openNFTModal = ({
    chainId,
    contractAddress,
    tokenId,
    initialData,
  }: {
    chainId: ChainType;
    contractAddress: Address;
    tokenId: bigint;
    initialData?: NFTViewerData;
  }) => {
    try {
      // Try to use collection slug if available
      const collection = getCollectionByAddress(contractAddress, chainId);
      const addressOrSlug = collection?.slug || contractAddress;
      const chainSlug = chainSettings[chainId]?.chainSlug || chainId;

      const url = `/nft/${chainSlug}/${addressOrSlug}/${tokenId.toString()}`;

      console.log('🔗 Opening NFT modal:', { chainId, chainSlug, contractAddress, tokenId: tokenId.toString(), url });

      // Store initial data in sessionStorage if provided to avoid re-fetching
      if (initialData) {
        const storageKey = `nft-data-${chainId}-${contractAddress}-${tokenId.toString()}`;
        sessionStorage.setItem(storageKey, JSON.stringify(initialData));
        console.log('💾 Stored NFT data in sessionStorage:', storageKey);
      }

      // Try router.push first, then router.replace as fallback
      try {
        router.push(url);
      } catch (pushError) {
        console.warn('⚠️ router.push failed, trying router.replace:', pushError);
        router.replace(url);
      }
    } catch (error) {
      console.error('❌ Error opening NFT modal:', error);
      // Fallback to window.open if navigation fails
      const collection = getCollectionByAddress(contractAddress, chainId);
      const addressOrSlug = collection?.slug || contractAddress;
      const chainSlug = chainSettings[chainId]?.chainSlug || chainId;
      const url = `/nft/${chainSlug}/${addressOrSlug}/${tokenId.toString()}`;
      window.open(url, '_blank');
    }
  };

  const openNFTPage = ({
    chainId,
    contractAddress,
    tokenId,
  }: {
    chainId: ChainType;
    contractAddress: Address;
    tokenId: bigint;
  }) => {
    // Try to use collection slug if available
    const collection = getCollectionByAddress(contractAddress, chainId);
    const addressOrSlug = collection?.slug || contractAddress;
    const chainSlug = chainSettings[chainId]?.chainSlug || chainId;

    const url = `/nft/${chainSlug}/${addressOrSlug}/${tokenId.toString()}`;

    // Use window.open for full page view
    window.open(url, '_blank');
  };

  return {
    openNFTModal,
    openNFTPage,
  };
}